import React, { useEffect, useState } from 'react';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { payrollTakeOn } from '../../api';

const GROUPS = [
  ['Income & fringe benefits', (c) => /^3[678]/.test(c)],
  ['Deductions & contributions', (c) => /^40/.test(c) || c === '4474'],
  ['Tax, UIF & SDL', (c) => ['4102', '4116', '4141', '4142'].includes(c)],
];
const LEAVE = [['annual', 'Annual leave'], ['sick', 'Sick leave (current cycle)'], ['family', 'Family responsibility']];

export const TakeOnDialog = ({ employee, taxYear, codes, onOpenChange, onSaved, toast }) => {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!employee) return;
    const t = employee.takeOn;
    setF({ periodsPaid: t?.periodsPaid ?? '', upToDate: t?.upToDate || '', netPaid: t?.netPaid ?? '', codes: { ...(t?.codes || {}) }, leave: { ...(employee.leaveOpening || {}) } });
  }, [employee]);
  if (!employee || !f) return null;

  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    const codesOut = Object.fromEntries(Object.entries(f.codes).filter(([, v]) => v !== '' && v != null).map(([k, v]) => [k, Number(v)]));
    const leave = Object.fromEntries(LEAVE.filter(([k]) => f.leave[k] !== '' && f.leave[k] != null).map(([k]) => [k, Number(f.leave[k])]));
    payrollTakeOn.save(employee.id, { taxYear, periodsPaid: Number(f.periodsPaid) || 0, upToDate: f.upToDate, netPaid: Number(f.netPaid) || 0, codes: codesOut, leave })
      .then(() => { toast({ title: 'Take-on balances saved', description: employee.name }); onOpenChange(false); onSaved(); })
      .catch((err) => toast({ title: 'Could not save', description: err?.response?.data?.detail?.toString() || 'Check the fields.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };
  const clear = () => window.confirm(`Remove take-on balances for ${employee.name}?`) &&
    payrollTakeOn.remove(employee.id, taxYear).then(() => { toast({ title: 'Take-on balances removed' }); onOpenChange(false); onSaved(); });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="takeon-dialog">
        <DialogHeader><DialogTitle>Take-on balances · {employee.name} · {taxYear - 1}/{String(taxYear).slice(2)}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div><Label htmlFor="to-up" className="text-xs">Balances up to (date) *</Label><Input id="to-up" type="date" required value={f.upToDate} onChange={(e) => setF({ ...f, upToDate: e.target.value })} className="mt-1 h-9" data-testid="takeon-uptodate-input"/></div>
            <div><Label htmlFor="to-p" className="text-xs">Pay periods already paid</Label><Input id="to-p" type="number" min="0" max="53" value={f.periodsPaid} onChange={(e) => setF({ ...f, periodsPaid: e.target.value })} className="mt-1 h-9" data-testid="takeon-periods-input"/></div>
            <div><Label htmlFor="to-n" className="text-xs">Net pay already paid (for YTD)</Label><Input id="to-n" type="number" min="0" step="0.01" value={f.netPaid} onChange={(e) => setF({ ...f, netPaid: e.target.value })} className="mt-1 h-9" data-testid="takeon-netpaid-input"/></div>
          </div>
          {GROUPS.map(([title, test]) => (
            <div key={title}>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mb-2">{title}</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {codes.filter((c) => test(c.code)).map((c) => (
                  <div key={c.code}>
                    <Label htmlFor={`to-${c.code}`} className="text-xs"><span className="font-mono text-stone-500">{c.code}</span> {c.label}</Label>
                    <Input id={`to-${c.code}`} type="number" min="0" step="0.01" value={f.codes[c.code] ?? ''} onChange={(e) => setF({ ...f, codes: { ...f.codes, [c.code]: e.target.value } })} className="mt-1 h-9" data-testid={`takeon-code-${c.code}`}/>
                  </div>
                ))}
              </div>
            </div>
          ))}
          <div data-testid="takeon-leave-section">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-800 mb-2">Opening leave balances (days, as at the date above)</div>
            <div className="grid grid-cols-3 gap-3">
              {LEAVE.map(([k, l]) => (
                <div key={k}>
                  <Label htmlFor={`to-lv-${k}`} className="text-xs">{l}</Label>
                  <Input id={`to-lv-${k}`} type="number" step="0.25" value={f.leave[k] ?? ''} onChange={(e) => setF({ ...f, leave: { ...f.leave, [k]: e.target.value } })} className="mt-1 h-9" data-testid={`takeon-leave-${k}`}/>
                </div>
              ))}
            </div>
          </div>
          <p className="text-xs text-stone-500">Enter the year-to-date totals from the previous payroll system. 4141 is employee + employer UIF combined. These feed the IRP5, EMP501 and payslip YTD, and later PAYE is calculated cumulatively so any under- or over-deduction is corrected over the remaining months.</p>
          <DialogFooter>
            {employee.takeOn && <Button type="button" variant="ghost" className="text-red-700 mr-auto" onClick={clear} data-testid="takeon-remove-btn"><Trash2 size={14} className="mr-1.5"/> Remove</Button>}
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="takeon-save-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Save balances</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
